<template>
  <ap-modal
    class="upgrade-modal-wrapper"
    :body-style="{
      background: '#f5f5f5',
      padding: '32px',
      borderRadius: '12px'
    }"
    centered
    :width="plansList.length === 3 ? '950px' : '45rem'"
    v-bind="$attrs"
    v-model:open="visibleModel"
    :footer="null"
    :title="null">
    <div class="upgrade-modal">
      <div class="mb-8 text-2xl font-bold text-black/85">
        {{ $t("components.modal.pricing.title") }}
      </div>
      <div
        :class="[
          'rounded-3xl bg-[#f5f5f5] gap-[10px] justify-center grid md:grid-rows-1 grid-cols-1 grid-rows-3',
          {
            'md:grid-cols-3': plansList.length === 3,
            'md:grid-cols-2': plansList.length === 2
          }
        ]">
        <div
          v-for="(plan, planKey) in plansList"
          :key="planKey"
          :class="[
            ' rounded-xl bg-white p-6 min-h-[382px] h-[382px] w-full max-w-xs',
            { 'border-solid p-5 border-primary border-[3px]': plan.featured }
          ]">
          <div class="flex flex-col justify-between">
            <div>
              <div class="text-black text-[22px] font-semibold mb-1">
                {{ $t(`components.modal.upgrade.${plan.translationKey}.title`) }}
              </div>
              <div class="text-[#191D23] text-xs font-semibold leading-normal mb-3">
                {{ $t(`components.modal.upgrade.${plan.translationKey}.subtitle`) }}
              </div>
              <div style="height: 180px">
                <div
                  class="flex items-center mb-[5px] gap-[10px]"
                  v-for="(feature, featureKey) in plan.features"
                  :key="featureKey">
                  <div class="modal-body__item-icon">
                    <img :src="checkCircle" alt="check" />
                  </div>

                  <div
                    class="text-xs font-medium text-black"
                    v-html="
                      $t(`components.modal.upgrade.${plan.translationKey}.${feature}.label`)
                    " />
                </div>
              </div>
            </div>
            <div class="relative flex flex-col justify-end">
              <ap-button
                size="large"
                block
                @click="() => handleClose(plan)"
                :class="[
                  'text-primary',
                  {
                    'bg-primary text-white border-primary': plan.featured
                  }
                ]">
                {{
                  plan.selected
                    ? $t("components.modal.upgrade.free.btn")
                    : $t("component.button.select.plan.label")
                }}
              </ap-button>
              <span
                class="mx-0 my-5 text-xs font-medium leading-normal text-black"
                v-if="plan.disclaimer">
                {{
                  plan.isPremium
                    ? $t(`components.modal.upgrade.${plan.translationKey}.disclaimer`)
                    : ""
                }}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div
        class="text-[#555] text-xs font-semibold leanding-[18px] my-5 mx-0"
        v-html="$t('components.modal.upgrade.disclaimer')" />
    </div>
  </ap-modal>
</template>

<script setup>
import { computed, watch } from "vue"
import { useRouter } from "vue-router"
import { storeToRefs } from "pinia"
import { useAppStore } from "@/stores/app"
import OpenSupportChatHandler from "@/handlers/OpenSupportChatHandler"
import { useUserAccountsStore } from "@/stores/userAccounts.js"
import { features } from "@/interfaces/planFeatures.js"
import checkCircle from "@/assets/icons/checkCircle.svg?url"

const visibleModel = defineModel("open", { default: false, type: Boolean })
const { userPlan } = storeToRefs(useUserAccountsStore())
const { plans } = storeToRefs(useAppStore())

const router = useRouter()

const plansList = computed(() => {
  const validPlan = ["free", "pro1", "pro2", "growth", "custom"]

  let _plans = plans.value?.length ? plans.value : []

  let plansFiltered = _plans
    .filter((plan) => plan.isActive && validPlan.includes(plan.translationKey))
    .map((plan) => {
      return {
        ...plan,
        key: plan.translationKey,
        features: features[plan.translationKey],
        featured: userPlan.value?.level + 1 === plan.level,
        selected: plan.iri === userPlan.value?.iri,
        disclaimer: ["pro1", "pro2"].includes(plan.translationKey)
      }
    })

  const userPlanIndex = userPlan.value?.uuid
    ? plansFiltered?.findIndex((plan) => plan.uuid === userPlan.value?.uuid)
    : 0

  return userPlanIndex + 1 === plansFiltered.length
    ? [
        ...plansFiltered,
        {
          featured: true,
          translationKey: "more",
          features: ["links", "other"],
          openSupportChat: true
        }
      ].splice(userPlanIndex, 3)
    : plansFiltered.splice(userPlanIndex, 3)
})

function handleClose(plan) {
  visibleModel.value = false

  if (plan.openSupportChat) return OpenSupportChatHandler.apply()

  if (!plan.selected) router.push({ name: "checkout", params: { planUuid: plan.uuid } })
}

watch(
  () => visibleModel.value,
  () => {
    if (visibleModel.value) {
      setTimeout(() => {
        let elem = document.getElementById("upgrade-modal-disclaimer")
        elem.onclick = OpenSupportChatHandler.apply
      }, 100)
    }
  },
  { immediate: true }
)
</script>

<style lang="postcss">
.upgrade-modal-wrapper {
  .ant-modal-content {
    background: transparent;
    padding: 0;

    #upgrade-modal-disclaimer {
      @apply text-primary underline cursor-pointer;
    }
  }
}
</style>
